 
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	AutoSuggest,
	CloseIcon,
	IconButton,
	ListItem,
	SelectValue,
	Text
} from '@phonectas/unifon-ui-kit';
import Highlighter from 'react-highlight-words';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {CdRatorOwnerSelectorProps, Contact} from './types';
import {RowProps} from '@phonectas/unifon-ui-kit/dist/molecules/AutoSuggest/types';

const CdRatorOwnerSelector: React.FC<CdRatorOwnerSelectorProps> = ({variant, onChange,  value}) => {
	const [t] = useTranslation();
	const isEmail = variant === 'mail';
	const isPhone = variant === 'phone';
	const {contacts} = useThreeSixtyNewStore((state) => ({
		contacts: state.cdRatorOwnerSelector?.customer_contacts,
	}), shallow);

	const contactsOptions = useMemo(() => {
		if (!contacts || !Array.isArray(contacts)) return [];
		const search = value.toLowerCase();
		return contacts.filter((contact) => {
			if (!contact) return false;
			const name = contact.name?.toLowerCase() || '';
			const email = contact.email?.toLowerCase() || '';
			const mobile = contact.phone_number?.toLowerCase() || '';
			return (
				name.includes(search) ||
                email.includes(search) ||
                mobile.includes(search)
			);
		});
	}, [contacts, value]);

	const handleChange = useCallback(
		(
			val: SelectValue | Contact,
			_e: React.SyntheticEvent,
			_name: string
		) => {
			if (val == null) {
				onChange('');
				return;
			}
			if (Array.isArray(val)) {
				// Handle both string[] and number[]
				onChange(val.join(', '));
			} else if (typeof val === 'string' || typeof val === 'number') {
				onChange(String(val));
			} else if (typeof val === 'object') {
				if (isEmail) {
					onChange(val.email);
				}
				if (isPhone) {
					onChange(val.phone_number);
				}
			} else {
				onChange(String(val));
			}
		}, [onChange, isEmail, isPhone]);

	const handleClear = useCallback(() => {
		onChange('');
	}, [onChange]);

	const renderOption = useCallback(
		({data: {onClick, collection}, index, style}: RowProps) => {
			const option = collection[index];

			if (!option) {
				return <span />;
			}

			const selectValue = isEmail ? option.email : option.phone_number;

			return (
				<ListItem
					value={selectValue}
					style={style}
					onClick={(_value, event) => onClick(selectValue, event)}
					primaryText={
						<Text variant="h5">
							<Highlighter autoEscape searchWords={[value]} textToHighlight={option.name} />
						</Text>
					}
					secondaryText={
						<Text color="textSecondary" variant="caption">
							{variant === 'mail' ? (
								<Highlighter autoEscape searchWords={[value]} textToHighlight={option.email} />
							) : (
								<Highlighter autoEscape searchWords={[value]} textToHighlight={option.phone_number} />
							)}
						</Text>
					}
				/>
			);
		}, [value, variant, isEmail]);

	return (
		<AutoSuggest 
			value={value}
			itemSize={50}
			onChange={handleChange}
			// filterBy={['name', 'mobile', 'email']}
			disableFiltering
			fullWidth
			// onKeyPress={onKeyPress}
			renderItem={renderOption}
			collection={contactsOptions || []}
			maxDropdownHeight={300}
			placeholder={t('Add recipient')}
			endAdornment={
				<div>
					{value && (
						<IconButton onClick={handleClear}>
							<CloseIcon />
						</IconButton>
					)}
				</div>
			}
		/>  
	);
}

export default CdRatorOwnerSelector;
